import { useEffect, useRef, useState, type ReactNode } from "react";
import { MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * SHARED PRESENTATION PRIMITIVES (spec §88). Small, dumb components that give
 * every screen the same page header, metric, meter, empty state, callout and
 * dialog. They format nothing financial on their own: callers pass the value
 * an engine already produced.
 */

export function BrandMark({ size = 28 }: { size?: number }) {
  // A roofline over a rising line: a house that is being improved.
  return (
    <svg className="brand-mark" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#2f7fc4" />
      <path d="M7 15.5 16 8l9 7.5" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 23.5l4.2-4.2 3 3 4.8-5.3" fill="none" stroke="#bfe0ff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PageHeader({
  eyebrow,
  title,
  lede,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <span className="page-eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {lede && <p className="muted page-lede">{lede}</p>}
      </div>
      {actions && <span className="page-actions">{actions}</span>}
    </div>
  );
}

export type Tone = "neutral" | "good" | "bad" | "attention" | "brand" | "unknown";

export function Metric({
  label,
  value,
  sub,
  tone = "neutral",
  emphasis,
  icon: Icon,
  title,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  emphasis?: boolean;
  icon?: LucideIcon;
  title?: string;
}) {
  const toneClass = tone === "good" ? "value-good" : tone === "bad" ? "value-bad" : tone === "attention" ? "value-attention" : "";
  return (
    <div className={`metric ${emphasis ? "metric-emphasis" : ""}`} title={title}>
      <span className="metric-label">
        {Icon && <Icon size={14} aria-hidden="true" />}
        {label}
      </span>
      <span className={`metric-value ${toneClass}`}>{value}</span>
      {sub && <span className="metric-sub">{sub}</span>}
    </div>
  );
}

/** A proportion bar. `fraction` is clamped to [0, 1]; over marks an overrun. */
export function Meter({ fraction, over, label }: { fraction: number; over?: boolean; label: string }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(fraction) ? fraction : 0)) * 100;
  return (
    <div
      className={`meter ${over ? "meter-over" : ""}`}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
    >
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  children,
  actions,
}: {
  icon: LucideIcon;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="panel empty-state">
      <span className="empty-illustration" aria-hidden="true">
        <Icon size={30} strokeWidth={1.75} />
      </span>
      <h2>{title}</h2>
      {children && <p className="muted">{children}</p>}
      {actions && <div className="empty-actions">{actions}</div>}
    </div>
  );
}

export function Callout({
  tone = "brand",
  icon: Icon,
  children,
}: {
  tone?: "brand" | "attention" | "bad" | "good";
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <div className={`callout ${tone === "brand" ? "" : `callout-${tone}`}`} role={tone === "bad" ? "alert" : "note"}>
      {Icon && <Icon size={16} aria-hidden="true" />}
      <div>{children}</div>
    </div>
  );
}

export function Badge({ tone = "neutral", children, dot }: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  const cls = tone === "neutral" ? "" : `badge-${tone}`;
  return <span className={`badge ${cls} ${dot ? "badge-dot" : ""}`}>{children}</span>;
}

/** A segmented control: one choice among a few views. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: T; label: ReactNode }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={o.id === value} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A modal dialog: focus moves in on open, Escape closes, focus returns to
 * whatever opened it, and Tab stays inside.
 */
export function Dialog({
  title,
  onClose,
  children,
  labelledBy = "dialog-title",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusable = () =>
      node ? [...node.querySelectorAll<HTMLElement>("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])")].filter((el) => !el.hasAttribute("disabled")) : [];
    focusable()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const items = focusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={labelledBy} ref={ref}>
        <h2 id={labelledBy}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * A "More" menu for secondary actions, so a row or header shows its one or
 * two everyday actions and keeps the rest a click away. It closes on Escape,
 * on an outside click, or when an item calls close() — a two-step confirm
 * inside it can therefore stay armed.
 */
export function OverflowMenu({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span className="overflow-menu" ref={ref}>
      <button type="button" className="btn btn-small btn-ghost btn-icon" aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen(!open)}>
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {open && (
        <span className="overflow-menu-list" role="menu">
          {children(() => setOpen(false))}
        </span>
      )}
    </span>
  );
}
